import React, { useEffect, useRef, useState } from "react";
import { Bell, Package, UserPlus, Boxes, X } from "lucide-react";
import { formatDateTime, formatPrice } from "../../lib/orderStatus";

const POLL_MS = 30000;

export default function NotificationBell({ apiAuth, onNavigate }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await apiAuth("/admin/notifications");
            setData(data);
        } catch {
            // silent — the bell is a nice-to-have; failing shouldn't blow up the shell
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on click outside
    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    // On open — mark orders + leads as seen after a short delay (so the user
    // actually reads them). Low stock stays as-is (it's a state).
    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => {
            apiAuth("/admin/notifications/mark-seen", {
                method: "POST",
                data: { kind: "all" },
            })
                .then(() => {
                    // Reload so the badge clears (only low_stock will remain)
                    load();
                })
                .catch(() => {});
        }, 1500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const total = data?.total ?? 0;
    const newOrders = data?.new_orders || [];
    const newLeads = data?.new_leads || [];
    const lowStock = data?.low_stock || [];
    const hasStream = newOrders.length + newLeads.length > 0;

    const go = (tab) => {
        setOpen(false);
        onNavigate?.(tab);
    };

    return (
        <div className="relative" ref={wrapRef} data-testid="admin-notification-bell">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`relative inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${
                    total > 0
                        ? "bg-brand-orange-soft hover:bg-brand-orange/20"
                        : "bg-brand-cream hover:bg-brand-green-soft"
                }`}
                data-testid="notification-bell-trigger"
                aria-label="Notificações"
            >
                <Bell
                    className={`h-4 w-4 ${
                        total > 0 ? "text-brand-orange animate-pulse" : "text-brand-ink-soft"
                    }`}
                />
                {total > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
                        data-testid="notification-badge"
                    >
                        {total > 9 ? "9+" : total}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[320px] sm:w-[380px] rounded-2xl bg-white shadow-2xl ring-1 ring-brand-green/10 z-30 overflow-hidden"
                    data-testid="notification-panel"
                >
                    <div className="px-4 py-3 border-b border-brand-green/10 flex items-center justify-between">
                        <div className="font-display font-medium text-brand-ink">
                            Notificações
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="p-1 rounded-full hover:bg-brand-cream"
                            data-testid="notification-close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {total === 0 && (
                            <div
                                className="p-8 text-center text-sm text-brand-ink-soft"
                                data-testid="notification-empty"
                            >
                                Tudo tratado. ✨
                            </div>
                        )}

                        {newOrders.length > 0 && (
                            <Section
                                icon={Package}
                                title={`Novas encomendas (${newOrders.length})`}
                                accent="text-brand-green"
                                onSeeAll={() => go("orders")}
                                testid="notification-section-orders"
                            >
                                {newOrders.slice(0, 5).map((o) => (
                                    <button
                                        type="button"
                                        onClick={() => go("orders")}
                                        key={o.id}
                                        className="w-full text-left px-4 py-2.5 hover:bg-brand-cream/60 transition-colors"
                                        data-testid={`notification-order-${o.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-brand-ink truncate">
                                                #{o.order_number || o.id?.slice(0, 8)} —{" "}
                                                {o.customer_name || o.customer_email || "Cliente"}
                                            </span>
                                            <span className="text-sm font-bold text-brand-green shrink-0">
                                                {formatPrice(o.amount || 0)}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-brand-ink-soft">
                                            {formatDateTime(o.created_at)}
                                        </div>
                                    </button>
                                ))}
                            </Section>
                        )}

                        {newLeads.length > 0 && (
                            <Section
                                icon={UserPlus}
                                title={`Novos leads (${newLeads.length})`}
                                accent="text-brand-orange"
                                onSeeAll={() => go("leads")}
                                testid="notification-section-leads"
                            >
                                {newLeads.slice(0, 5).map((l) => (
                                    <button
                                        type="button"
                                        onClick={() => go("leads")}
                                        key={l.id}
                                        className="w-full text-left px-4 py-2.5 hover:bg-brand-cream/60 transition-colors"
                                        data-testid={`notification-lead-${l.id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-brand-ink truncate">
                                                {l.name}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-cream text-brand-ink shrink-0">
                                                {l.interest}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-brand-ink-soft truncate">
                                            {l.email} · {formatDateTime(l.created_at)}
                                        </div>
                                    </button>
                                ))}
                            </Section>
                        )}

                        {lowStock.length > 0 && (
                            <Section
                                icon={Boxes}
                                title={`Stock crítico (${lowStock.length})`}
                                accent="text-red-600"
                                onSeeAll={() => go("stock")}
                                testid="notification-section-stock"
                            >
                                {lowStock.slice(0, 5).map((s) => (
                                    <button
                                        type="button"
                                        onClick={() => go("stock")}
                                        key={s.product_id}
                                        className="w-full text-left px-4 py-2.5 hover:bg-brand-cream/60 transition-colors"
                                        data-testid={`notification-stock-${s.product_id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-brand-ink truncate">
                                                {s.name}
                                            </span>
                                            <span className="text-[11px] font-bold text-red-600 shrink-0">
                                                {s.quantity} un
                                                <span className="text-brand-ink-soft font-normal ml-1">
                                                    (limiar {s.low_threshold})
                                                </span>
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </Section>
                        )}
                    </div>
                    {hasStream && (
                        <div className="px-4 py-2 border-t border-brand-green/10 text-[11px] text-brand-ink-soft text-center">
                            As novidades são marcadas como vistas ao abrir este painel.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const Section = ({ icon: Icon, title, accent, onSeeAll, children, testid }) => (
    <div className="border-b border-brand-green/10 last:border-b-0" data-testid={testid}>
        <div className="px-4 py-2 flex items-center justify-between bg-brand-cream/40">
            <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${accent}`}>
                <Icon className="h-3 w-3" /> {title}
            </div>
            <button
                type="button"
                onClick={onSeeAll}
                className="text-[10px] font-bold uppercase tracking-widest text-brand-green hover:underline"
            >
                Ver todas
            </button>
        </div>
        <div className="divide-y divide-brand-green/5">{children}</div>
    </div>
);
